import { Empty, Skeleton, Timeline } from 'antd';
import { useGetEncounterList } from 'src/hooks/encounter';
import { Appointment, Encounter, Patient } from 'src/models';
import { AudioOutlined } from '@ant-design/icons';
import { useGetAppointmentList } from 'src/hooks/appointment';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@frontegg/react';

type PatientTimelineProps = {
  patient: Patient;
};

export const PatientTimeline = ({ patient }: PatientTimelineProps) => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const {
    data: encounterData,
    isLoading: encounterLoading,
    error: encounterError,
  } = useGetEncounterList({
    patientId: patient.patient_id,
  });

  const {
    data: appointmentData,
    isLoading: appointmentLoading,
    error: appointmentError,
  } = useGetAppointmentList({
    patientId: patient.patient_id,
    patientName: `${patient.first_name} ${patient.last_name}`,
  });

  if (encounterLoading || appointmentLoading) {
    return <Skeleton active />;
  }

  if (encounterError || appointmentError) {
    return (
      <div className="text-center text-gray-500">Something went wrong</div>
    );
  }

  if (
    !encounterData?.encounters.length &&
    !appointmentData?.appointments.length
  ) {
    return <Empty description="No records found for the patient" />;
  }

  const renderEncounter = (encounter: Encounter) => {
    const encounterCreatedAt = new Date(encounter.created_at);
    return {
      children: (
        <div
          className="flex bg-white w-[480px] max-w-full shadow-sm rounded-lg"
          onClick={() => {
            // @ts-expect-error
            analytics.track('Clicked Encounter From Patient Timeline', {
              email: user?.email,
              patient_id: patient.patient_id,
            });
          }}
        >
          <div className="flex flex-col border-r border-gray-200 p-2 flex-shrink-0 text-right">
            <span className="font-medium text-xl">
              {encounterCreatedAt.toLocaleString('en-US', {
                day: '2-digit',
              })}
            </span>
            <span>
              {encounterCreatedAt.toLocaleString('en-US', {
                month: 'short',
              })}
              ,{' '}
              {encounterCreatedAt.toLocaleString('en-US', {
                year: 'numeric',
              })}
            </span>
          </div>
          <div className="flex flex-col p-2">
            <div className="font-medium text-lg">{encounter.description}</div>
            <div className="text-gray-500 flex items-center gap-1">
              <AudioOutlined />
              {encounter.practitioner_name}
            </div>
          </div>
        </div>
      ),
    };
  };

  const renderAppointment = (appointment: Appointment) => {
    const {
      start_date_local,
      practitioner_firstName,
      practitioner_lastName,
      practitioner_title,
      condition_name,
      appt_uuid,
    } = appointment;

    const appointmentStartedAt = new Date(start_date_local);

    const practitionerName = [
      practitioner_firstName,
      practitioner_lastName,
      practitioner_title,
    ]
      .filter(Boolean)
      .join(' ');

    return {
      children: (
        <div
          className="flex bg-white w-[480px] max-w-full shadow-sm rounded-lg cursor-pointer"
          onClick={() => {
            // @ts-expect-error
            analytics.track('Clicked Appointment From Patient Timeline', {
              email: user?.email,
              patient_id: patient.patient_id,
            });
            navigate(`/schedule/${appt_uuid}`);
          }}
        >
          <div className="flex flex-col border-r border-gray-200 p-2 flex-shrink-0 text-right">
            <span className="font-medium text-xl">
              {appointmentStartedAt.toLocaleString('en-US', {
                day: '2-digit',
              })}
            </span>
            <span>
              {appointmentStartedAt.toLocaleString('en-US', {
                month: 'short',
              })}
              ,{' '}
              {appointmentStartedAt.toLocaleString('en-US', {
                year: 'numeric',
              })}
            </span>
          </div>
          <div className="flex flex-col p-2">
            <div className="font-medium text-lg">{condition_name}</div>
            <div className="text-gray-500 flex items-center gap-1">
              <AudioOutlined />
              {practitionerName}
            </div>
          </div>
        </div>
      ),
    };
  };

  return (
    <Timeline
      mode="left"
      items={[
        ...(encounterData?.encounters.map(renderEncounter) || []),
        ...(appointmentData?.appointments.map(renderAppointment) || []),
      ]}
    />
  );
};
